import { ALIGNMENT_EDGES } from '../constants';

/**
 * Object-to-object alignment strategy
 * Detects and shows alignment lines between objects
 */
export const objectAlignmentStrategy = {
  /**
   * Check for alignments between active object and other objects
   * @param {fabric.Object} activeObject - The object being moved
   * @param {fabric.Canvas} canvas - The fabric canvas
   * @param {Object} options - Alignment options
   * @param {string} context - Context of the operation ('moving', 'scaling', etc.)
   * @returns {Object} Alignment data with lines array
   */
  checkAlignment(activeObject, canvas, options, context) {
    const alignmentLines = [];
    const activeBounds = activeObject.getBoundingRect();
    const activeCenter = {
      x: activeBounds.left + activeBounds.width / 2,
      y: activeBounds.top + activeBounds.height / 2,
    };

    // Get all canvas objects
    const canvasObjects = canvas.getObjects();

    // Check alignment with each object on canvas
    for (const obj of canvasObjects) {
      // Skip self and excluded objects
      if (obj === activeObject) continue;
      // Don't skip MagicFrame objects - they should participate in alignment
      // but still skip the internal editingImg of MagicFrames
      if (obj._magicFrame !== undefined) continue; // Skip editingImg objects
      if (obj.excludeFromExport && obj.type !== 'MagicFrame') continue;
      if (obj.isGuideline) continue;

      const objBounds = obj.getBoundingRect();
      const objCenter = {
        x: objBounds.left + objBounds.width / 2,
        y: objBounds.top + objBounds.height / 2,
      };

      // Check vertical alignments (for vertical lines)
      const verticalAlignments = this._checkVerticalAlignments(
        activeCenter,
        activeBounds,
        objCenter,
        objBounds,
        options
      );

      // Add vertical alignment lines
      for (const alignment of verticalAlignments) {
        if (alignment.isAligned) {
          // Calculate the Y extent of the line (should span both objects)
          const minY = Math.min(activeBounds.top, objBounds.top) - 20; // Add some padding
          const maxY =
            Math.max(
              activeBounds.top + activeBounds.height,
              objBounds.top + objBounds.height
            ) + 20; // Add some padding

          alignmentLines.push({
            type: 'vertical',
            x: alignment.x,
            y1: minY,
            y2: maxY,
            alignmentType: alignment.type,
            isSnapped: alignment.isSnapped,
            color: alignment.isSnapped
              ? options.snapIndicatorColor ||
                options.objectAlignmentLineColor ||
                options.lineColor
              : options.objectAlignmentLineColor || options.lineColor,
            width: alignment.isSnapped
              ? (options.aligningLineWidth || options.lineWidth) *
                (options.snappedLineWidthMultiplier || 1.2)
              : options.aligningLineWidth || options.lineWidth,
          });
        }
      }

      // Check horizontal alignments (for horizontal lines)
      const horizontalAlignments = this._checkHorizontalAlignments(
        activeCenter,
        activeBounds,
        objCenter,
        objBounds,
        options
      );

      // Add horizontal alignment lines
      for (const alignment of horizontalAlignments) {
        if (alignment.isAligned) {
          // Calculate the X extent of the line (should span both objects)
          const minX = Math.min(activeBounds.left, objBounds.left) - 20; // Add some padding
          const maxX =
            Math.max(
              activeBounds.left + activeBounds.width,
              objBounds.left + objBounds.width
            ) + 20; // Add some padding

          alignmentLines.push({
            type: 'horizontal',
            y: alignment.y,
            x1: minX,
            x2: maxX,
            alignmentType: alignment.type,
            isSnapped: alignment.isSnapped,
            color: alignment.isSnapped
              ? options.snapIndicatorColor ||
                options.objectAlignmentLineColor ||
                options.lineColor
              : options.objectAlignmentLineColor || options.lineColor,
            width: alignment.isSnapped
              ? (options.aligningLineWidth || options.lineWidth) *
                (options.snappedLineWidthMultiplier || 1.2)
              : options.aligningLineWidth || options.lineWidth,
          });
        }
      }
    }

    // Only show distance measurements if enabled AND object is moving
    if (options.showDistanceMeasurements && context === 'moving') {
      // Check canvas edges
      if (options.enableCanvasEdgeAlignment !== false) {
        this._checkCanvasEdgeAlignments(
          activeBounds,
          canvas,
          options,
          alignmentLines
        );
      }

      // Get nearby objects for distance measurements
      const nearbyObjects = this._getNearbyObjects(
        activeObject,
        activeBounds,
        canvasObjects,
        options
      );

      // Check each nearby object for distance measurements
      for (const obj of nearbyObjects) {
        const objBounds = obj.getBoundingRect();

        // Check if this object is obstructed by another
        if (
          this._isObjectObstructed(activeBounds, objBounds, nearbyObjects, obj)
        ) {
          continue;
        }

        // Check for distance measurements between objects
        this._checkObjectDistances(
          activeBounds,
          objBounds,
          options,
          alignmentLines
        );
      }
    }

    return { lines: alignmentLines };
  },

  /**
   * Get nearby objects within the distance measurement threshold
   * @param {fabric.Object} activeObject - The object being moved
   * @param {Object} activeBounds - Bounding rect of active object
   * @param {Array} canvasObjects - All canvas objects
   * @param {Object} options - Alignment options
   * @returns {Array} Array of nearby objects
   */
  _getNearbyObjects(activeObject, activeBounds, canvasObjects, options) {
    const nearbyObjects = [];
    const threshold = options.distanceMeasurementThreshold || 300;

    for (const obj of canvasObjects) {
      // Skip self and excluded objects
      if (obj === activeObject) continue;
      // Don't skip MagicFrame objects - they should participate in alignment
      // but still skip the internal editingImg of MagicFrames
      if (obj._magicFrame !== undefined) continue; // Skip editingImg objects
      if (obj.excludeFromExport && obj.type !== 'MagicFrame') continue;
      if (obj.isGuideline) continue;

      const objBounds = obj.getBoundingRect();

      // Calculate minimum distance between objects
      const horizontalDist = Math.max(
        0,
        Math.max(
          objBounds.left - (activeBounds.left + activeBounds.width),
          activeBounds.left - (objBounds.left + objBounds.width)
        )
      );
      const verticalDist = Math.max(
        0,
        Math.max(
          objBounds.top - (activeBounds.top + activeBounds.height),
          activeBounds.top - (objBounds.top + objBounds.height)
        )
      );

      const minDistance = Math.sqrt(
        horizontalDist * horizontalDist + verticalDist * verticalDist
      );

      // Only include objects within threshold distance
      if (minDistance <= threshold) {
        nearbyObjects.push(obj);
      }
    }

    return nearbyObjects;
  },

  /**
   * Check if an object is obstructed by another object between it and the active object
   * @param {Object} activeBounds - Bounding rect of active object
   * @param {Object} targetBounds - Bounding rect of target object
   * @param {Array} nearbyObjects - Array of nearby objects
   * @param {fabric.Object} targetObj - The target object
   * @returns {boolean} True if obstructed
   */
  _isObjectObstructed(activeBounds, targetBounds, nearbyObjects, targetObj) {
    // Calculate center points
    const activeCenter = {
      x: activeBounds.left + activeBounds.width / 2,
      y: activeBounds.top + activeBounds.height / 2,
    };
    const targetCenter = {
      x: targetBounds.left + targetBounds.width / 2,
      y: targetBounds.top + targetBounds.height / 2,
    };

    // Check each nearby object to see if it blocks the path
    for (const obj of nearbyObjects) {
      if (obj === targetObj) continue;

      const objBounds = obj.getBoundingRect();

      // Check if this object is between active and target
      if (
        this._isObjectBetween(
          activeCenter,
          targetCenter,
          activeBounds,
          targetBounds,
          objBounds
        )
      ) {
        return true;
      }
    }

    return false;
  },

  /**
   * Check if an object is between two other objects
   * @param {Object} activeCenter - Center of active object
   * @param {Object} targetCenter - Center of target object
   * @param {Object} activeBounds - Bounds of active object
   * @param {Object} targetBounds - Bounds of target object
   * @param {Object} obstacleBounds - Bounds of potential obstacle
   * @returns {boolean} True if obstacle is between active and target
   */
  _isObjectBetween(
    activeCenter,
    targetCenter,
    activeBounds,
    targetBounds,
    obstacleBounds
  ) {
    // For horizontal measurements (objects side by side)
    if (
      Math.abs(activeCenter.y - targetCenter.y) <
      Math.abs(activeCenter.x - targetCenter.x)
    ) {
      // Check if obstacle is horizontally between the two objects
      const leftObj =
        activeBounds.left < targetBounds.left ? activeBounds : targetBounds;
      const rightObj =
        activeBounds.left < targetBounds.left ? targetBounds : activeBounds;

      // Obstacle must be between the objects horizontally
      if (
        obstacleBounds.left <= leftObj.left + leftObj.width ||
        obstacleBounds.left + obstacleBounds.width >= rightObj.left
      ) {
        return false;
      }

      // Check if obstacle overlaps vertically with the measurement line
      const overlapTop = Math.max(activeBounds.top, targetBounds.top);
      const overlapBottom = Math.min(
        activeBounds.top + activeBounds.height,
        targetBounds.top + targetBounds.height
      );

      // If there's vertical overlap and obstacle is in that range
      if (overlapTop < overlapBottom) {
        return (
          obstacleBounds.top < overlapBottom &&
          obstacleBounds.top + obstacleBounds.height > overlapTop
        );
      }
    }

    // For vertical measurements (objects above/below)
    else {
      // Check if obstacle is vertically between the two objects
      const topObj =
        activeBounds.top < targetBounds.top ? activeBounds : targetBounds;
      const bottomObj =
        activeBounds.top < targetBounds.top ? targetBounds : activeBounds;

      // Obstacle must be between the objects vertically
      if (
        obstacleBounds.top <= topObj.top + topObj.height ||
        obstacleBounds.top + obstacleBounds.height >= bottomObj.top
      ) {
        return false;
      }

      // Check if obstacle overlaps horizontally with the measurement line
      const overlapLeft = Math.max(activeBounds.left, targetBounds.left);
      const overlapRight = Math.min(
        activeBounds.left + activeBounds.width,
        targetBounds.left + targetBounds.width
      );

      // If there's horizontal overlap and obstacle is in that range
      if (overlapLeft < overlapRight) {
        return (
          obstacleBounds.left < overlapRight &&
          obstacleBounds.left + obstacleBounds.width > overlapLeft
        );
      }
    }

    return false;
  },

  /**
   * Check distances from canvas edges
   */
  _checkCanvasEdgeAlignments(activeBounds, canvas, options, alignmentLines) {
    const canvasWidth = canvas.getWidth();
    const canvasHeight = canvas.getHeight();

    // Distance from left edge
    const leftDistance = activeBounds.left;
    if (leftDistance > 0) {
      alignmentLines.push({
        type: 'horizontal', // Horizontal line for left-right distance
        y: activeBounds.top + activeBounds.height / 2, // Center vertically on object
        x1: 0,
        x2: activeBounds.left,
        alignmentType: 'canvas-left',
        isSnapped: false,
        color: options.lineColor,
        width: options.lineWidth,
        distance: leftDistance,
      });
    }

    // Distance from right edge
    const rightDistance =
      canvasWidth - (activeBounds.left + activeBounds.width);
    if (rightDistance > 0) {
      alignmentLines.push({
        type: 'horizontal', // Horizontal line for left-right distance
        y: activeBounds.top + activeBounds.height / 2, // Center vertically on object
        x1: activeBounds.left + activeBounds.width,
        x2: canvasWidth,
        alignmentType: 'canvas-right',
        isSnapped: false,
        color: options.lineColor,
        width: options.lineWidth,
        distance: rightDistance,
      });
    }

    // Distance from top edge
    const topDistance = activeBounds.top;
    if (topDistance > 0) {
      alignmentLines.push({
        type: 'vertical', // Vertical line for top-bottom distance
        x: activeBounds.left + activeBounds.width / 2, // Center horizontally on object
        y1: 0,
        y2: activeBounds.top,
        alignmentType: 'canvas-top',
        isSnapped: false,
        color: options.lineColor,
        width: options.lineWidth,
        distance: topDistance,
      });
    }

    // Distance from bottom edge
    const bottomDistance =
      canvasHeight - (activeBounds.top + activeBounds.height);
    if (bottomDistance > 0) {
      alignmentLines.push({
        type: 'vertical', // Vertical line for top-bottom distance
        x: activeBounds.left + activeBounds.width / 2, // Center horizontally on object
        y1: activeBounds.top + activeBounds.height,
        y2: canvasHeight,
        alignmentType: 'canvas-bottom',
        isSnapped: false,
        color: options.lineColor,
        width: options.lineWidth,
        distance: bottomDistance,
      });
    }
  },

  /**
   * Check distances between objects
   */
  _checkObjectDistances(activeBounds, objBounds, options, alignmentLines) {
    // Calculate edges
    const activeLeft = activeBounds.left;
    const activeRight = activeBounds.left + activeBounds.width;
    const activeTop = activeBounds.top;
    const activeBottom = activeBounds.top + activeBounds.height;

    const objLeft = objBounds.left;
    const objRight = objBounds.left + objBounds.width;
    const objTop = objBounds.top;
    const objBottom = objBounds.top + objBounds.height;

    // Check if objects are roughly horizontally aligned (overlapping Y range)
    const verticalOverlap = !(activeBottom < objTop || activeTop > objBottom);

    if (verticalOverlap) {
      // Check left-to-right distance
      if (objLeft > activeRight) {
        const distance = objLeft - activeRight;
        // Find vertical center of overlapping region
        const overlapTop = Math.max(activeTop, objTop);
        const overlapBottom = Math.min(activeBottom, objBottom);
        const centerY = (overlapTop + overlapBottom) / 2;

        alignmentLines.push({
          type: 'horizontal', // Horizontal line for horizontal distance
          y: centerY,
          x1: activeRight,
          x2: objLeft,
          alignmentType: 'left-to-right',
          isSnapped: false,
          color: options.lineColor,
          width: options.lineWidth,
          distance: distance,
        });
      }

      // Check right-to-left distance
      if (activeLeft > objRight) {
        const distance = activeLeft - objRight;
        // Find vertical center of overlapping region
        const overlapTop = Math.max(activeTop, objTop);
        const overlapBottom = Math.min(activeBottom, objBottom);
        const centerY = (overlapTop + overlapBottom) / 2;

        alignmentLines.push({
          type: 'horizontal', // Horizontal line for horizontal distance
          y: centerY,
          x1: objRight,
          x2: activeLeft,
          alignmentType: 'right-to-left',
          isSnapped: false,
          color: options.lineColor,
          width: options.lineWidth,
          distance: distance,
        });
      }
    }

    // Check if objects are roughly vertically aligned (overlapping X range)
    const horizontalOverlap = !(activeRight < objLeft || activeLeft > objRight);

    if (horizontalOverlap) {
      // Check top-to-bottom distance
      if (objTop > activeBottom) {
        const distance = objTop - activeBottom;
        // Find horizontal center of overlapping region
        const overlapLeft = Math.max(activeLeft, objLeft);
        const overlapRight = Math.min(activeRight, objRight);
        const centerX = (overlapLeft + overlapRight) / 2;

        alignmentLines.push({
          type: 'vertical', // Vertical line for vertical distance
          x: centerX,
          y1: activeBottom,
          y2: objTop,
          alignmentType: 'top-to-bottom',
          isSnapped: false,
          color: options.lineColor,
          width: options.lineWidth,
          distance: distance,
        });
      }

      // Check bottom-to-top distance
      if (activeTop > objBottom) {
        const distance = activeTop - objBottom;
        // Find horizontal center of overlapping region
        const overlapLeft = Math.max(activeLeft, objLeft);
        const overlapRight = Math.min(activeRight, objRight);
        const centerX = (overlapLeft + overlapRight) / 2;

        alignmentLines.push({
          type: 'vertical', // Vertical line for vertical distance
          x: centerX,
          y1: objBottom,
          y2: activeTop,
          alignmentType: 'bottom-to-top',
          isSnapped: false,
          color: options.lineColor,
          width: options.lineWidth,
          distance: distance,
        });
      }
    }
  },

  /**
   * Check vertical alignments between objects
   */
  _checkVerticalAlignments(
    activeCenter,
    activeBounds,
    objCenter,
    objBounds,
    options
  ) {
    const alignments = [];
    const tolerance = options.alignmentTolerance;
    const snapThreshold = options.snapThreshold || 5;

    // Center alignment
    const centerDistance = Math.abs(activeCenter.x - objCenter.x);
    if (centerDistance <= tolerance) {
      alignments.push({
        type: ALIGNMENT_EDGES.CENTER_VERTICAL,
        x: objCenter.x,
        isAligned: true,
        isSnapped: centerDistance <= snapThreshold,
      });
    }

    // Left edge alignment
    const leftActiveEdge = activeBounds.left;
    const leftObjEdge = objBounds.left;
    const leftDistance = Math.abs(leftActiveEdge - leftObjEdge);
    if (leftDistance <= tolerance) {
      alignments.push({
        type: ALIGNMENT_EDGES.LEFT,
        x: leftObjEdge,
        isAligned: true,
        isSnapped: leftDistance <= snapThreshold,
      });
    }

    // Right edge alignment
    const rightActiveEdge = activeBounds.left + activeBounds.width;
    const rightObjEdge = objBounds.left + objBounds.width;
    const rightDistance = Math.abs(rightActiveEdge - rightObjEdge);
    if (rightDistance <= tolerance) {
      alignments.push({
        type: ALIGNMENT_EDGES.RIGHT,
        x: rightObjEdge,
        isAligned: true,
        isSnapped: rightDistance <= snapThreshold,
      });
    }

    // Edge touching alignments
    // Left edge of active to right edge of object
    const leftToRightDistance = Math.abs(leftActiveEdge - rightObjEdge);
    if (leftToRightDistance <= tolerance) {
      alignments.push({
        type: 'left-to-right',
        x: rightObjEdge,
        isAligned: true,
        isSnapped: leftToRightDistance <= snapThreshold,
      });
    }

    // Right edge of active to left edge of object
    const rightToLeftDistance = Math.abs(rightActiveEdge - leftObjEdge);
    if (rightToLeftDistance <= tolerance) {
      alignments.push({
        type: 'right-to-left',
        x: leftObjEdge,
        isAligned: true,
        isSnapped: rightToLeftDistance <= snapThreshold,
      });
    }

    return alignments;
  },

  /**
   * Check horizontal alignments between objects
   */
  _checkHorizontalAlignments(
    activeCenter,
    activeBounds,
    objCenter,
    objBounds,
    options
  ) {
    const alignments = [];
    const tolerance = options.alignmentTolerance;
    const snapThreshold = options.snapThreshold || 5;

    // Center alignment
    const centerDistance = Math.abs(activeCenter.y - objCenter.y);
    if (centerDistance <= tolerance) {
      alignments.push({
        type: ALIGNMENT_EDGES.CENTER_HORIZONTAL,
        y: objCenter.y,
        isAligned: true,
        isSnapped: centerDistance <= snapThreshold,
      });
    }

    // Top edge alignment
    const topActiveEdge = activeBounds.top;
    const topObjEdge = objBounds.top;
    const topDistance = Math.abs(topActiveEdge - topObjEdge);
    if (topDistance <= tolerance) {
      alignments.push({
        type: ALIGNMENT_EDGES.TOP,
        y: topObjEdge,
        isAligned: true,
        isSnapped: topDistance <= snapThreshold,
      });
    }

    // Bottom edge alignment
    const bottomActiveEdge = activeBounds.top + activeBounds.height;
    const bottomObjEdge = objBounds.top + objBounds.height;
    const bottomDistance = Math.abs(bottomActiveEdge - bottomObjEdge);
    if (bottomDistance <= tolerance) {
      alignments.push({
        type: ALIGNMENT_EDGES.BOTTOM,
        y: bottomObjEdge,
        isAligned: true,
        isSnapped: bottomDistance <= snapThreshold,
      });
    }

    // Edge touching alignments
    // Top edge of active to bottom edge of object
    const topToBottomDistance = Math.abs(topActiveEdge - bottomObjEdge);
    if (topToBottomDistance <= tolerance) {
      alignments.push({
        type: 'top-to-bottom',
        y: bottomObjEdge,
        isAligned: true,
        isSnapped: topToBottomDistance <= snapThreshold,
      });
    }

    // Bottom edge of active to top edge of object
    const bottomToTopDistance = Math.abs(bottomActiveEdge - topObjEdge);
    if (bottomToTopDistance <= tolerance) {
      alignments.push({
        type: 'bottom-to-top',
        y: topObjEdge,
        isAligned: true,
        isSnapped: bottomToTopDistance <= snapThreshold,
      });
    }

    return alignments;
  },

  /**
   * Get alignment lines for this strategy
   * @returns {Array} Array of line definitions
   */
  getAlignmentLines() {
    // This method is called by AlignmentManager if needed
    // Currently handled in checkAlignment
    return [];
  },
};
